import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader, Plug, ExternalLink, Info, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { embeddedSignupService } from '../../services/embeddedSignupService';
import { useNotification } from '../../hooks/useNotification';
import { useSearchParams } from 'react-router-dom';

const EmbeddedSignup: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showInfo, NotificationComponent } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [processingCallback, setProcessingCallback] = useState(false);
  const [signupStatus, setSignupStatus] = useState<'idle' | 'initializing' | 'logging_in' | 'processing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Verificar se App ID está configurado
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    if (!appId) {
      setSignupStatus('error');
      setErrorMessage('REACT_APP_FACEBOOK_APP_ID não configurado. Configure a variável de ambiente.');
      showError('Configuração Necessária', 'App ID do Facebook não configurado. Configure REACT_APP_FACEBOOK_APP_ID nas variáveis de ambiente.');
      setLoading(false);
      return;
    }
    
    setLoading(false);
    
    // Verificar se há código de callback na URL
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorReason = searchParams.get('error_reason');
    
    if (error) {
      setSignupStatus('error');
      setErrorMessage(errorReason || error || 'Erro desconhecido no processo de autenticação');
      showError('Erro na Autenticação', errorReason || error || 'Ocorreu um erro durante o processo de autenticação com a Meta.');
      // Limpar parâmetros da URL após um delay para garantir que a mensagem seja exibida
      setTimeout(() => {
        setSearchParams({});
      }, 3000);
    } else if (code) {
      handleOAuthCallback(code);
      // Limpar parâmetros da URL após processar
      setTimeout(() => {
        setSearchParams({});
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const handleConnect = async () => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    if (!appId) {
      showError('Configuração Necessária', 'App ID do Facebook não configurado. Configure REACT_APP_FACEBOOK_APP_ID nas variáveis de ambiente.');
      return;
    }

    try {
      setSignupStatus('initializing');
      setErrorMessage(null);

      // Inicializar Facebook SDK
      await embeddedSignupService.initializeFacebookSDK();
      
      setSignupStatus('logging_in');
      
      // Fazer login com Facebook
      const authResponse = await embeddedSignupService.loginWithFacebook();
      
      setSignupStatus('processing');
      
      // Iniciar Embedded Signup
      embeddedSignupService.startEmbeddedSignup(authResponse.accessToken);
      
    } catch (error: any) {
      console.error('Erro ao iniciar conexão:', error);
      setSignupStatus('error');
      setErrorMessage(error.message || 'Erro ao iniciar processo de conexão');
      showError('Erro', error.message || 'Erro ao iniciar processo de conexão');
    }
  };

  const handleOAuthCallback = async (code: string) => {
    if (!userProfile?.empresaCnpj) {
      showError('Erro', 'Usuário não encontrado');
      return;
    }

    try {
      setProcessingCallback(true);
      setSignupStatus('processing');
      setErrorMessage(null);

      // Nota: A troca de code por token requer App Secret, que deve estar no backend
      // Por enquanto, vamos mostrar uma mensagem informativa
      showInfo(
        'Processamento Necessário',
        'O código de autorização foi recebido. Para completar a integração, é necessário trocar o code por access token usando o App Secret no backend. Esta funcionalidade requer implementação adicional no servidor.'
      );

      // TODO: Implementar chamada ao backend para trocar code por token
      // const appSecret = process.env.REACT_APP_FACEBOOK_APP_SECRET; // NÃO fazer isso - App Secret deve estar no backend
      // const result = await embeddedSignupService.handleOAuthCallback(code, appSecret);
      // await firebaseService.saveEmbeddedSignupResult(userProfile.empresaCnpj, result);
      // setSignupStatus('success');
      // showSuccess('Sucesso', 'WhatsApp Business conectado com sucesso!');

      setSignupStatus('idle');
    } catch (error: any) {
      console.error('Erro ao processar callback:', error);
      setSignupStatus('error');
      setErrorMessage(error.message || 'Erro ao processar callback');
      showError('Erro', error.message || 'Erro ao processar callback');
    } finally {
      setProcessingCallback(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Plug className="w-6 h-6" />
              Integração Assistida - Meta Embedded Signup
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Conecte seu WhatsApp Business Account automaticamente através do fluxo da Meta
            </p>
          </div>
        </div>

        {/* Instruções */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Como funciona:</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-200">
                <li>Configure o App ID do seu App no Meta Developers</li>
                <li>Clique em "Conectar WhatsApp Business"</li>
                <li>Faça login com sua conta Facebook Business</li>
                <li>Complete o processo de onboarding (selecionar Business Manager, criar/selecionar WABA, verificar número)</li>
                <li>O sistema receberá automaticamente os tokens e IDs necessários</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Informações do App */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informações do App</h3>
          
          {process.env.REACT_APP_FACEBOOK_APP_ID ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>App ID:</strong> {process.env.REACT_APP_FACEBOOK_APP_ID}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Redirect URI:</strong> {window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                    ? `${window.location.origin}/integracao/embedded-signup/callback`
                    : 'https://painel.swimmingpool.com.br/integracao/embedded-signup/callback'}
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-sm text-red-800 dark:text-red-200">
                  <strong>Erro:</strong> App ID não configurado. Configure a variável de ambiente <code className="bg-red-100 dark:bg-red-900/40 px-1 rounded">REACT_APP_FACEBOOK_APP_ID</code>.
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Importante:</strong> Configure no Meta Developers:
                <ol className="list-decimal list-inside mt-2 space-y-2">
                  <li>
                    <strong>Domínios do Host JSSDK:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>Acesse: <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Meta Developers</a> → Seu App → Configurações → Básicas</li>
                      <li>Em <strong>"Domínios do Host JSSDK"</strong>, adicione: <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">painel.swimmingpool.com.br</code></li>
                      <li>Para desenvolvimento, adicione também: <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">localhost</code></li>
                    </ul>
                  </li>
                  <li>
                    <strong>Redirect URI (Facebook Login):</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>Acesse: Facebook Login → Configurações</li>
                      <li>Adicione em "URIs de Redirecionamento Válidos do OAuth":</li>
                      <li>Produção: <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">https://painel.swimmingpool.com.br/integracao/embedded-signup/callback</code></li>
                      <li>Desenvolvimento: <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">http://localhost:3000/integracao/embedded-signup/callback</code></li>
                    </ul>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-sm text-red-800 dark:text-red-200">
                  <strong>⚠️ Erro: "O domínio do host JSSDK é desconhecido"</strong>
                  <p className="mt-2">Para corrigir em produção:</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>Acesse <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Meta Developers</a></li>
                    <li>Selecione o app "chat-fireagent"</li>
                    <li>Vá em <strong>Configurações</strong> → <strong>Básicas</strong></li>
                    <li>Em <strong>"Domínios do Host JSSDK"</strong> (ou "JSSDK Host Domains"), adicione: <code className="bg-red-100 dark:bg-red-900/40 px-1 rounded font-mono">painel.swimmingpool.com.br</code></li>
                    <li>Salve as alterações</li>
                    <li>Recarregue esta página após salvar</li>
                  </ol>
                  <p className="mt-2 text-xs italic">Nota: Pode levar alguns minutos para as alterações serem aplicadas.</p>
                </div>
              </div>
            </div>
          )}
          
          {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-sm text-red-800 dark:text-red-200">
                  <strong>⚠️ Erro de Conexão Segura:</strong> O Facebook requer HTTPS. Para desenvolvimento local:
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>Acesse <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Meta Developers</a></li>
                    <li>Selecione o app "chat-fireagent"</li>
                    <li>Vá em <strong>Configurações</strong> → <strong>Básicas</strong></li>
                    <li>Em <strong>"Domínios do Host JSSDK"</strong>, adicione: <code className="bg-red-100 dark:bg-red-900/40 px-1 rounded">localhost</code></li>
                    <li>Certifique-se de que o app está em <strong>Modo de Desenvolvimento</strong> (não em Produção)</li>
                    <li>Ou use um túnel HTTPS como <a href="https://ngrok.com" target="_blank" rel="noopener noreferrer" className="underline">ngrok</a> para testar localmente</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botão de Conexão */}
        {process.env.REACT_APP_FACEBOOK_APP_ID && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Conectar WhatsApp Business</h3>
            
            {signupStatus === 'success' && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">WhatsApp Business conectado com sucesso!</span>
                </div>
              </div>
            )}

            {signupStatus === 'error' && errorMessage && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2 text-red-800 dark:text-red-200">
                  <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold block mb-2">Erro: {errorMessage}</span>
                    <button
                      onClick={() => {
                        setSignupStatus('idle');
                        setErrorMessage(null);
                        setSearchParams({});
                      }}
                      className="text-sm underline hover:no-underline"
                    >
                      Tentar novamente
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <button
                onClick={handleConnect}
                disabled={signupStatus === 'initializing' || signupStatus === 'logging_in' || signupStatus === 'processing' || processingCallback}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {(signupStatus === 'initializing' || signupStatus === 'logging_in' || signupStatus === 'processing' || processingCallback) ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    {signupStatus === 'initializing' && 'Inicializando...'}
                    {signupStatus === 'logging_in' && 'Fazendo login...'}
                    {(signupStatus === 'processing' || processingCallback) && 'Processando...'}
                  </>
                ) : (
                  <>
                    <Plug className="w-5 h-5" />
                    Conectar WhatsApp Business
                  </>
                )}
              </button>

              <a
                href="https://developers.facebook.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ExternalLink className="w-4 h-4" />
                Meta Developers
              </a>
            </div>

            {signupStatus === 'idle' && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                Clique no botão acima para iniciar o processo de conexão. Você será redirecionado para a interface da Meta.
              </p>
            )}
          </div>
        )}
      </div>

      <NotificationComponent />
    </div>
  );
};

export default EmbeddedSignup;

